import { useCallback, useEffect, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { administrationApi, type AccountIdentity, type AssignmentUpdate } from '@/services/instanceAdministration'
import { ApiError } from '@/services/http/errors'
import { ADMIN_DATA } from './useAccess'

type SaveRequest = { command: AssignmentUpdate; controller: AbortController }
const statusOf = (error: unknown) => (error as ApiError | null)?.status

export const useAccounts = (userId: string, onAccessFailure: () => void) => {
  const client = useQueryClient()
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [identity, setIdentity] = useState<AccountIdentity | null>(null)
  const pending = useRef(new Map<string, { controller: AbortController; promise: Promise<Awaited<ReturnType<typeof administrationApi.save>>> }>())
  const base = [...ADMIN_DATA, userId]
  const previewKey = [...base, 'assignment', identity?.source, identity?.user_id]
  const directory = useQuery({ queryKey: [...base, 'instances'],
    queryFn: ({ signal }) => administrationApi.instances(signal), retry: false })
  const accounts = useQuery({ queryKey: [...base, 'accounts', query, page],
    queryFn: ({ signal }) => administrationApi.accounts(query, page, signal),
    retry: false, placeholderData: keepPreviousData })
  const preview = useQuery({ queryKey: previewKey, enabled: identity !== null,
    queryFn: ({ signal }) => {
      if (!identity) throw new TypeError('请选择账号')
      return administrationApi.assignment(identity, signal)
    }, retry: false })
  const mutation = useMutation({ gcTime: 0,
    mutationFn: async ({ command, controller }: SaveRequest) => {
      try {
        const accepted = await administrationApi.save(command, controller.signal)
        controller.signal.throwIfAborted()
        return accepted
      } catch (error) {
        controller.signal.throwIfAborted()
        const status = statusOf(error)
        if (status && status < 500) throw error
        const current = await administrationApi.assignment({ source: command.source, user_id: command.user_id }, controller.signal)
        controller.signal.throwIfAborted()
        if (current.exists && current.instance_id === command.instance_id && current.status === command.status) return current
        if (current.revision !== command.expected_revision) throw new ApiError('账号分配已变化，请重新读取', { status: 409 })
        throw error
      }
    },
    onSuccess: (accepted, { controller }) => {
      if (controller.signal.aborted) return
      client.setQueryData([...ADMIN_DATA, userId, 'assignment', accepted.source, accepted.user_id], accepted)
      void client.invalidateQueries({ queryKey: [...ADMIN_DATA, userId, 'accounts'] })
    },
    onError: (error, { controller }) => {
      if (!controller.signal.aborted && [401, 403].includes(statusOf(error) ?? 0)) onAccessFailure()
    },
  })
  useEffect(() => () => {
    pending.current.forEach(({ controller }) => controller.abort())
    pending.current.clear()
  }, [])
  useEffect(() => {
    if ([directory.error, accounts.error, preview.error].some((error) => [401, 403].includes(statusOf(error) ?? 0))) onAccessFailure()
  }, [accounts.error, directory.error, preview.error, onAccessFailure])
  const { mutateAsync, reset } = mutation
  const { refetch: refetchPreview } = preview
  const { refetch: refetchAccounts } = accounts
  const { refetch: refetchDirectory } = directory
  const save = useCallback((command: AssignmentUpdate) => {
    const key = JSON.stringify(command)
    const existing = pending.current.get(key)
    if (existing) return existing.promise
    const controller = new AbortController()
    const promise = mutateAsync({ command, controller }).finally(() => { pending.current.delete(key) })
    pending.current.set(key, { controller, promise })
    return promise
  }, [mutateAsync])
  const select = useCallback((selected: AccountIdentity) => {
    reset()
    setIdentity({ source: selected.source, user_id: selected.user_id })
  }, [reset])
  const search = useCallback((value: string) => { setPage(1); setQuery(value) }, [])
  const reload = useCallback(async () => {
    await Promise.all([refetchDirectory(), refetchAccounts(), identity ? refetchPreview() : Promise.resolve()])
    reset()
  }, [identity, refetchAccounts, refetchDirectory, refetchPreview, reset])
  return { directory, accounts, preview, identity, select, save, search, query, page, setPage, reload,
    saving: mutation.isPending, saveError: mutation.error, saved: mutation.isSuccess }
}
